// A bare `<v-chart>` bar chart — no card, like BaseDonutChart: the page owns the
// card, the title and any metric toggle. One bar series over a category axis,
// vertical or horizontal, coloured from the theme's `chart.series` palette.
//
// It replaces the app's `DocumentFileTypeBarChart.vue` (vertical, one colour per
// bar, value labels) and `TeamUserDocumentBarChart.vue` (horizontal, one colour per
// series, click-to-drill) — both of which aggregated their own rows, carried their
// own palette literals and leaked a hand-rolled window resize listener. The
// aggregation moved to the app (`composables/useDocumentCharts.js`), the colours to
// `@rdrive/theme`, and the resize to `<v-chart autoresize>`.
//
//   props:  rows          (Array)    [{ id?, label, value, color? }] in display order
//           orientation   (String)   'vertical' (categories on x) | 'horizontal' (on y)
//           seriesName    (String)   ECharts series name / tooltip line label — already translated
//           valueAxisName (String)   value-axis title — already translated
//           formatValue   (Function) (value) => string; tooltip, value-axis labels, bar labels
//           showLabels    (Boolean)  a value label on each bar
//           colorBy       (String)   'series' (one colour for all bars) | 'bar' (one per bar)
//           colorIndex    (Number)   index into chart.series for colorBy: 'series'
//           borderRadius  (Number)   rounded bar end, in px (0 = square)
//           labelRotate   (Number)   category-axis label rotation
//           height        (Number)   chart height in px
//           onSelect      (Function) (row) => void on bar click — a function prop, no emit
//   opt-in (AM_ABSORPTION_PLAN step C4; every one off by default — the option is unchanged):
//           row.color     (String)   a row's own bar colour (over colorBy/colorIndex)
//           selectedId    (String|Number) the selected row's id: every other bar is dimmed to
//                                    the theme's `dimOpacity` (click-to-filter feedback)
//           formatLabel   (Function) (value, row) => string; the bar labels (over formatValue)
//           formatTooltip (Function) (row, point) => html; the whole tooltip
//           valueMin / valueMax / valueMinInterval (Number) the value axis' min / max / minInterval
//           barMaxWidth   (Number)   the bars' max width in px
//   opt-in (AM_ABSORPTION_PLAN step D2; off by default — the option is unchanged):
//           labelInterval (Number|String) the category labels' interval ('auto' = ECharts thins
//                                    them so they never overlap; null = every label, as before)
//   opt-in (AM_ABSORPTION_PLAN step E3; off by default — the option is unchanged):
//           gridRight     (Number|String) the plot's right margin (px, or '8%'), room for the value
//                                    labels of horizontal bars; null = the axis skeleton's '4%'
//   opt-in (2026-10-16, PNG export on every chart; off by default — nothing rendered changes):
//           exportFilename (String)  the PNG's file name; '' = `downloadPng()` does nothing
//           exportTitle    (String)  the PNG's title ('' = none: a bare chart has no title of its own)
//   setup:  downloadPng()  saves the chart as a PNG (no legend) — a bare chart renders no button:
//                          its page owns the card and calls this through a template ref
//
// No emits (the donut's convention: a click calls `onSelect`), no strings and no
// `messages/` — the tooltip is the caller's `seriesName` plus `formatValue` (or the
// caller's `formatTooltip`), and there is no `chartFontColor` prop: colours come from
// `useChartTheme()`.
//
// Full contract: ../../../README.md → BaseBarChart. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below —
// both are what the template's `<v-chart>` needs — so it lives here and ./setup.js
// stays pure logic. The two SFCs this component replaces also registered
// `TitleComponent` and `LegendComponent`; nothing in the option ever used a title
// or a legend, so they are dropped.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { TooltipComponent, GridComponent } from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([CanvasRenderer, BarChart, TooltipComponent, GridComponent]);

export default {
  name: 'base-bar-chart',
  props: {
    // [{ id?, label, value, color? }] in display order. Reactive BY REFERENCE: the adapter
    // hands over a new array on every change, so there is no deep watch here. A row's
    // `color` (opt-in) paints that bar, over colorBy/colorIndex.
    rows: {
      type: Array,
      required: true
    },
    // 'vertical': categories on x, values on y. 'horizontal': the other way round
    orientation: {
      type: String,
      default: 'vertical',
      validator: (v) => ['vertical', 'horizontal'].includes(v)
    },
    // ECharts series name, shown in the tooltip — already translated by the page
    seriesName: {
      type: String,
      default: ''
    },
    // Value-axis title — already translated by the page
    valueAxisName: {
      type: String,
      default: ''
    },
    // (value) => string, for the tooltip, the value-axis labels and the bar labels
    formatValue: {
      type: Function,
      default: (value) => String(value)
    },
    // A value label on each bar ('top' when vertical, 'right' when horizontal)
    showLabels: {
      type: Boolean,
      default: false
    },
    // 'series': every bar is chart.series[colorIndex]. 'bar': bar i is chart.series[i % length]
    colorBy: {
      type: String,
      default: 'series',
      validator: (v) => ['series', 'bar'].includes(v)
    },
    colorIndex: {
      type: Number,
      default: 0
    },
    // Rounded bar end in px — `[r,r,0,0]` vertical, `[0,r,r,0]` horizontal. The
    // file-type chart has 5, the team/user chart 0 (square, as today).
    borderRadius: {
      type: Number,
      default: 0
    },
    labelRotate: {
      type: Number,
      default: 0
    },
    height: {
      type: Number,
      default: 400
    },
    // (row) => void on a bar click; null disables clicking
    onSelect: {
      type: Function,
      default: null
    },
    // --- Opt-in (AM_ABSORPTION_PLAN step C4, asset-management's EChartCard bar charts). Each
    // one defaults off, and off adds nothing to the option: project-overview's Documents bars
    // are byte-for-byte what they were.
    // The selected row's `id` (a click-to-filter selection the page owns): every OTHER bar is
    // drawn at the theme's `dimOpacity`. null = no selection, nothing dimmed.
    selectedId: {
      type: [String, Number],
      default: null
    },
    // (value, row) => string for the bar labels (e.g. '' to hide a zero); null = formatValue
    formatLabel: {
      type: Function,
      default: null
    },
    // (row, point) => html for the whole tooltip; null = '<label>' + seriesName + formatValue
    formatTooltip: {
      type: Function,
      default: null
    },
    // The value axis' min / max / minInterval (e.g. 0 / 100 for a percentage, minInterval 1
    // for counts); null = ECharts scales the axis
    valueMin: {
      type: Number,
      default: null
    },
    valueMax: {
      type: Number,
      default: null
    },
    valueMinInterval: {
      type: Number,
      default: null
    },
    // The bars' maximum width in px (a few bars in a wide card); null = ECharts' width
    barMaxWidth: {
      type: Number,
      default: null
    },
    // --- Opt-in (AM_ABSORPTION_PLAN step D2, asset-management's Inspections Due — 13 month bars in
    // a narrow card). Off adds nothing: the category axis keeps `interval: 0` (every label).
    // The category labels' `interval`: 'auto' lets ECharts thin them so they never overlap (AM's
    // `hideOverlap`); a number shows every (n+1)th; null = every label
    labelInterval: {
      type: [Number, String],
      default: null
    },
    // --- Opt-in (AM_ABSORPTION_PLAN step E3, asset-management's Outcome Distribution and Completeness
    // — horizontal bars labelled "n (x%)" / "83%" on their right). Off adds nothing: the grid is the
    // axis skeleton's. The plot's right margin, px or a percentage string: a horizontal bar's value
    // label sits beyond its end, and the skeleton's '4%' clips it for the longest bar (a 100% bar on
    // a 0–100 axis always); null = the skeleton's grid
    gridRight: {
      type: [Number, String],
      default: null
    },
    // --- Opt-in (2026-10-16, PNG export on every chart). No button here — the page owns the card
    // and its title row, and calls `downloadPng()` through a template ref.
    // The PNG's file name; '' = export off (`downloadPng()` is a no-op)
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title (the page's card title); '' = no title band
    exportTitle: {
      type: String,
      default: ''
    }
  },
  // `<v-chart>` was auto-registered from the import by `<script setup>`; a classic
  // component has to register it explicitly or Vue logs "Failed to resolve component:
  // v-chart" and renders nothing. Vuetify tags need no entry — vite-plugin-vuetify
  // rewrites the compiled render code, not the script block.
  components: { VChart },
  setup,
};
